/**
 * Copyright (c) 2010-2026 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { EnvironmentProviders, Provider } from '@angular/core';
import { provideRouter, Routes, withComponentInputBinding } from '@angular/router';
import { MapContainerComponent } from '@map/components/map-container.component';
import { RootComponent } from '../root/root.component';
import { AuthenticationComponent } from '../security/components/authentication.component';
import { ConfirmAccountComponent } from '../security/components/confirm-account/confirm-account.component';
import { DisclaimerComponent } from '../security/components/disclaimer/disclaimer.component';
import { ForgotPasswordComponent } from '../security/components/forgot-password/forgot-password.component';
import { LoginComponent } from '../security/components/login/login.component';
import { ResetPasswordComponent } from '../security/components/password/reset-password/reset-password.component';
import { AdminAreaGuard } from './guards/admin-area.guard';
import { AuthenticatedGuard } from './guards/authenticated.guard';
import { DisabledRouteGuard } from './guards/disabled-route.guard';
import { NoManualAuthenticationGuard } from './guards/no-manual-authentication.guard';
import { RootUrlGuard } from './guards/root-url.guard';

export const csCoreRoutes: Routes = [
    {
        path: '',
        component: RootComponent,
        pathMatch: 'full',
        runGuardsAndResolvers: 'always',
        canActivate: [ RootUrlGuard ]
    },
    {
        path: 'map',
        component: MapContainerComponent,
        canActivate: [ DisabledRouteGuard ],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'dashboard',
        loadChildren: () => import('../../modules/dashboard/dashboard.routes'),
        canMatch: [ DisabledRouteGuard ],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'admin',
        loadChildren: () => import('../../modules/admin/admin.module').then((m) => m.AdminModule),
        canMatch: [ AdminAreaGuard ],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'auth',
        component: AuthenticationComponent,
        canActivate: [ NoManualAuthenticationGuard ],
        children: [
            { path: 'login', component: LoginComponent },
            { path: 'disclaimer', component: DisclaimerComponent },
            { path: 'forgot', component: ForgotPasswordComponent },
            { path: 'reset', component: ResetPasswordComponent },
            { path: 'confirm', component: ConfirmAccountComponent }
        ]
    },
    { path: '**', redirectTo: '' }
];

export function provideAppRouting(): (Provider | EnvironmentProviders)[] {
    return [
        provideRouter(csCoreRoutes, withComponentInputBinding()),
        AuthenticatedGuard,
        AdminAreaGuard,
        RootUrlGuard,
        DisabledRouteGuard
    ];
}